// The canvas's waiting state while a new forecast runs: what Sayer is doing, how long it has
// taken, how long it usually takes, and a cancel that asks first. A run that streams its research
// (a Mirabilis lane, deep or quick) keeps the card open for the whole run and shows that research
// as it goes (DeepRunProgress). Pure props.
import React, { useEffect, useRef, useState } from "react";
import { C } from "../lib/colors.js";
import { ASK_HERO_PENDING_RUN_ID } from "../lib/forecast/askHero.js";
import { laneTier, tierDuration } from "../lib/forecast/engines.js";
import { RUN_STATUS_STAGES, runStatusStageIndex } from "../lib/forecast/progressSteps.js";
import {
  evidenceTiles,
  latestFinding,
  researchDurationCopy,
  researchOf,
  researchPhases,
  streamsResearch,
  triggersFound,
} from "../lib/forecast/deepProgress.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { Button } from "./ui/Primitives.jsx";
import { IconClock } from "./ui/Icons.jsx";
import DeepRunProgress from "./DeepRunProgress.jsx";
import RunStageRow from "./RunStageRow.jsx";

/** mm:ss, or h:mm:ss past an hour. */
function formatElapsed(ms) {
  const s = Math.max(0, Math.floor((Number.isFinite(ms) ? ms : 0) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${String(m).padStart(2, "0")}:${ss}`;
}

/**
 * The expected-duration line for a run that does not stream its research (one that does reads
 * researchDurationCopy); `late` once a Quick run has overrun. No email is sent when a run
 * finishes (alert emails are for tracked forecasts' scheduled updates), so the line promises
 * only that the run carries on.
 */
function durationCopy(tier, { late }) {
  const leave = "You can leave this page; it keeps running and appears in Forecasts.";
  if (late) return `This is taking longer than usual. ${leave}`;
  if (tier === "quick")
    return `Quick usually takes ${tierDuration("quick") || "a couple of minutes"}.`;
  if (tier === "deep") return `Deep can take ${tierDuration("deep") || "up to an hour"}. ${leave}`;
  return `This usually takes a few minutes. ${leave}`;
}

/**
 * "Cancel forecast", which asks before it acts. Shared by the run status card and the chat.
 * @param {{canCancel: boolean, onCancel: () => void, compact?: boolean}} props
 */
export function CancelForecastControl({ canCancel, onCancel, compact = false }) {
  const [confirming, setConfirming] = useState(false);
  const keepRef = useRef(null);
  useEffect(() => {
    if (confirming) keepRef.current?.focus();
  }, [confirming]);
  if (confirming) {
    // Escape keeps the run going, from either button (focus opens on Keep running).
    const onEscape = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setConfirming(false);
      }
    };
    return (
      <div
        role="group"
        aria-label="Confirm cancel"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 8,
          width: "100%",
        }}
      >
        <span
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            color: C.white,
            flex: "1 1 100%",
            textAlign: compact ? "left" : "right",
          }}
        >
          Cancel this forecast? Research so far will be lost.
        </span>
        <Button
          {...ui("run-status.keep-running")}
          ref={keepRef}
          size="sm"
          onClick={() => setConfirming(false)}
          onKeyDown={onEscape}
        >
          Keep running
        </Button>
        <Button
          {...ui("chat.cancel-forecast")}
          size="sm"
          variant="danger"
          onKeyDown={onEscape}
          onClick={() => {
            setConfirming(false);
            onCancel?.();
          }}
        >
          Cancel forecast
        </Button>
      </div>
    );
  }
  return (
    <Button
      {...ui("run-status.cancel")}
      size="sm"
      variant={compact ? "ghost" : "secondary"}
      disabled={!canCancel}
      title={canCancel ? undefined : "You can cancel once research has started"}
      onClick={() => setConfirming(true)}
    >
      Cancel forecast
    </Button>
  );
}

/**
 * @param {object} props
 * @param {object|null} props.run  the active root run (run-store Run); renders nothing when null
 * @param {boolean} props.visible  the caller's gate (showsRunStatus + layout conditions)
 * @param {string|number} [props.leftInset]  the left panel's width, so the card centres in
 *   the visible canvas
 * @param {boolean} props.canCancel
 * @param {() => void} props.onCancel
 * @param {{callId: string, text: string, done: boolean}[]} [props.questions]  a research run's
 *   research questions in plain English (hooks/useResearchQuestions.js)
 * @param {number} [props.nowMs]  the clock to read instead of the card's own (the dev replay)
 * @param {boolean} [props.liveProgress]  whether the research stream reaches the app
 *   (chatboard-forecast-live-progress); without it a research run shows the stage list
 */
export default function RunStatus({
  run,
  visible,
  leftInset = 0,
  canCancel,
  onCancel,
  questions = [],
  nowMs,
  liveProgress = false,
}) {
  const [tick, setTick] = useState(() => Date.now());
  const ownClock = nowMs == null;
  useEffect(() => {
    if (!visible || !ownClock) return undefined;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [visible, ownClock]);
  const now = ownClock ? tick : nowMs;
  if (!visible || !run) return null;

  const tier = laneTier(run.backend || "") || null;
  const research = liveProgress && streamsResearch(run);
  const stageIdx = runStatusStageIndex(tier, run.progress, run.status);
  // A manual reforecast reuses the run and its original createdAt; startedAt (stamped at
  // the click, or hydrated from the server's started_at after a reload) is when it began.
  const startedMs = Math.max(
    Date.parse(run.createdAt || "") || -Infinity,
    Date.parse(run.startedAt || "") || -Infinity,
  );
  const elapsed = Number.isFinite(startedMs) ? now - startedMs : 0;
  const late = tier === "quick" && elapsed > 4 * 60 * 1000;
  const question =
    run.contract?.question || run.contractQuestion || run.contract?.title || run.question || "";
  // The step line carries a live counter in parentheses ("round 3 of 12"); show it beside
  // the current stage rather than restating the stage.
  const detailMatch = /\(([^)]+)\)\s*$/.exec(run.progress?.message || "");
  const detail = stageIdx === 1 && detailMatch ? detailMatch[1] : null;
  // Keyed by the client run id, as the forecast's other telemetry is (not the server's
  // `forecastId`, which lands mid-run); a just-asked question's stand-in has none yet.
  const runKey = run.runId === ASK_HERO_PENDING_RUN_ID ? null : run.runId;

  return (
    <div
      data-testid="run-status"
      {...surface("run-status", runKey)}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: leftInset,
        right: 0,
        zIndex: 35,
        // Moves with the rail panel, not ahead of it.
        transition: "left var(--rail-slide)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        boxSizing: "border-box",
        background: `color-mix(in srgb, ${C.bg} 92%, transparent)`,
        cursor: "default",
        userSelect: "text",
        animation: "simpleFadeIn 0.3s ease-out",
      }}
    >
      <section
        aria-live="polite"
        aria-label="Forecast in progress"
        {...ui("run-status.card")}
        className="sy-card"
        style={{
          width: "100%",
          maxWidth: research ? 560 : 520,
          maxHeight: "100%",
          overflowY: "auto",
          padding: 24,
          boxSizing: "border-box",
          boxShadow: C.shadowBox,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            fontSize: 13,
            color: C.grayL,
          }}
        >
          <span style={{ fontWeight: 500 }}>Forecasting</span>
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.gray }}
            title="Time since you asked"
          >
            <IconClock width={14} height={14} />
            {/* The elapsed time, the steps and the duration copy change by themselves: no dead
                clicks from those mutations. The cancel control stays outside them, so its own
                confirmation counts as the response to a press. */}
            <span className="sy-num" data-telemetry-ticking="" style={{ color: C.grayL }}>
              {formatElapsed(elapsed)}
            </span>
          </span>
        </div>
        <h2
          style={{
            margin: "12px 0 0",
            fontSize: 15,
            lineHeight: 1.45,
            fontWeight: 500,
            color: C.white,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {question}
        </h2>
        <hr className="sy-divider" style={{ margin: "20px 0 12px" }} />
        {research ? (
          <DeepRunProgress
            key={runKey ?? "pending"}
            phases={researchPhases(run, now)}
            finding={latestFinding(run.narration ?? [])}
            // A quick run shows its source count only; a deep run also lists its evidence cards.
            evidence={tier === "deep" ? evidenceTiles(researchOf(run)) : []}
            questions={questions}
            triggers={triggersFound(run)}
          />
        ) : (
          <ol
            data-telemetry-ticking=""
            style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 2 }}
          >
            {RUN_STATUS_STAGES.map((label, i) => (
              <RunStageRow
                key={label}
                label={label}
                state={i < stageIdx ? "done" : i === stageIdx ? "current" : "todo"}
                detail={i === stageIdx ? detail : null}
              />
            ))}
          </ol>
        )}
        <p
          data-telemetry-ticking=""
          style={{ margin: "16px 0 0", fontSize: 13, lineHeight: 1.5, color: C.gray }}
        >
          {research ? researchDurationCopy(tier, elapsed) : durationCopy(tier, { late })}
        </p>
        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <CancelForecastControl canCancel={canCancel} onCancel={onCancel} />
        </div>
      </section>
    </div>
  );
}
